<script setup lang="ts">
/**
 * ProductOptionPicker — 고정 구성(FIXED_OPTIONS) 다중 선택 목록.
 * 체크박스 그룹 의미를 그대로 쓴다(네이티브 input). 보유·확정 대기 구성은 선택할 수 없고 사유 배지를 단다.
 * 표시 금액은 카탈로그의 단품가(서버 값) — 최종 결제 금액은 견적 패널이 서버 견적으로만 보여 준다.
 * maxSelectable(서버 maxSelectableCount)에 이르면 고르지 않은 구성을 잠근다 — 서버도 초과 주문을 거부한다(082).
 */
import { BaseBadge } from '#/components/ui';
import { won } from '#/data/pricing';
import type { CatalogOption } from '#/types/catalog';

const props = defineProps<{
  options: CatalogOption[];
  selectedIds: number[];
  ownedIds: number[];
  pendingIds: number[];
  /** 한 주문 최대 선택 수 — 없으면 제한 없음. */
  maxSelectable?: number;
}>();

/** 최대 선택 수에 이르러 더 고를 수 없는 구성(이미 고른 구성은 해제 가능). */
function limitLocked(id: number): boolean {
  return (
    props.maxSelectable !== undefined &&
    props.selectedIds.length >= props.maxSelectable &&
    !props.selectedIds.includes(id)
  );
}

function locked(id: number): boolean {
  return props.ownedIds.includes(id) || props.pendingIds.includes(id) || limitLocked(id);
}

const emit = defineEmits<{
  toggle: [optionId: number];
}>();
</script>

<template>
  <fieldset class="m-0 border-0 p-0">
    <legend class="sr-only">구매할 구성 선택</legend>
    <ul class="flex flex-col gap-3">
      <li v-for="opt in options" :key="opt.id">
        <label
          class="flex gap-4 rounded-card border bg-white p-5 shadow-sm transition-colors"
          :class="[
            selectedIds.includes(opt.id) ? 'border-accent' : 'border-line',
            locked(opt.id) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          ]"
        >
          <input
            type="checkbox"
            class="mt-1 h-[18px] w-[18px] flex-none accent-[var(--accent)]"
            :checked="selectedIds.includes(opt.id)"
            :disabled="locked(opt.id)"
            @change="emit('toggle', opt.id)"
          />
          <span class="min-w-0 flex-1">
            <span class="flex flex-wrap items-center gap-2">
              <b class="text-[15.5px] font-extrabold tracking-[-0.02em] text-brand">
                {{ opt.name }}
              </b>
              <BaseBadge v-if="opt.featured && opt.featuredReason" type="accent">
                {{ opt.featuredReason }}
              </BaseBadge>
              <BaseBadge v-if="ownedIds.includes(opt.id)" type="ok">보유 중</BaseBadge>
              <BaseBadge v-else-if="pendingIds.includes(opt.id)" type="warn">
                결제 확정 대기
              </BaseBadge>
            </span>
            <span class="mt-1 block text-[13px] leading-[1.6] text-body">{{
              opt.description
            }}</span>
            <span v-if="opt.commonScope" class="mt-2 block text-[12.5px] leading-[1.5] text-muted">
              <b class="font-extrabold text-brand">공통</b> {{ opt.commonScope }}
            </span>
            <span
              v-if="opt.additionalScope"
              class="mt-1 block text-[12.5px] leading-[1.5] text-muted"
            >
              <b class="font-extrabold text-brand">추가</b> {{ opt.additionalScope }}
            </span>
          </span>
          <span class="flex-none text-right">
            <span class="block text-[11.5px] text-muted">단품가</span>
            <span class="font-serif text-[18px] font-extrabold text-brand">
              {{ won(opt.listPrice) }}<span class="ml-0.5 text-[13px]">원</span>
            </span>
          </span>
        </label>
      </li>
    </ul>
  </fieldset>
</template>
